/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

/**
 * Debug affordances for stress-testing latency paths without editing product
 * code. The Debug Controls page (`/debug`) is the sole writer of the persisted
 * value here; every other module reads.
 *
 * - `artificialLatencySeconds$` — additional delay applied inside `gqlFetch`
 *   before every request. Persisted under `localStorage['artificialLatencySeconds']`.
 *
 * The `?force-error` test hook lives in `util/forceError.ts` (below this layer)
 * so `util/context.ts` can read it without a util→debug back-edge.
 */

import { data, type ObservableValue } from '@amazon/vinyl-observable'

/** localStorage key backing {@link artificialLatencySeconds$}. */
export const ARTIFICIAL_LATENCY_STORAGE_KEY = 'artificialLatencySeconds'

// Legit-raw (§4.13): synchronous-boot debug flag — the value seeds a module
// observable read by `gqlFetch` before the first request, so an async
// createPersistentItem `get()` would miss early requests.
function readArtificialLatency(): number {
    try {
        if (typeof localStorage === 'undefined') return 0
        const raw = localStorage.getItem(ARTIFICIAL_LATENCY_STORAGE_KEY)
        if (raw == null) return 0
        // Stored in seconds and may be fractional (e.g. 0.3s from a 300ms UI).
        const n = parseFloat(raw)
        if (Number.isFinite(n) && n >= 0) return n
        return 0
    } catch {
        return 0
    }
}

function writeArtificialLatency(next: number): void {
    try {
        if (typeof localStorage === 'undefined') return
        if (next > 0) {
            localStorage.setItem(ARTIFICIAL_LATENCY_STORAGE_KEY, String(next))
        } else {
            localStorage.removeItem(ARTIFICIAL_LATENCY_STORAGE_KEY)
        }
    } catch {
        // Private-browsing / storage-blocked fallback: silently swallow.
    }
}

const state$ = data(readArtificialLatency())

/**
 * Read-only observable of the debug latency delay, in seconds. `gqlFetch`
 * awaits this before issuing each request so callers can rehearse
 * skeleton / spinner behavior without a slow network.
 */
export const artificialLatencySeconds$: ObservableValue<number> = state$

/**
 * Persist a new latency value (in seconds) and update
 * {@link artificialLatencySeconds$} so live callers pick it up
 * immediately. Values are clamped to non-negative numbers; fractional
 * seconds are allowed (a 300ms UI preset persists as 0.3s).
 */
export function setArtificialLatencySeconds(next: number): void {
    const clamped = Math.max(0, Number.isFinite(next) ? next : 0)
    if (state$.value === clamped) return
    state$.value = clamped
    writeArtificialLatency(clamped)
}
